import { createAsyncThunk } from '@reduxjs/toolkit';
import { ConsultationDataActions } from 'applicaiton/constants/actions';
import { AxiosError } from 'axios';

import { ConsultationDataAPI } from 'integration/api/consultationData';
import {
  AppointmentDataCreationDto,
  FamilyHistoryCreationDto,
  SurgeryCreationDto,
} from '@docbay/schemas';
import { PaginationPropsWithSearch } from 'common/types/pagination';

/**
 * Fetches consultation data for a given appointment ID
 *
 * @param {string} appointmentId - The ID of the appointment to fetch data for
 * @returns {Promise<any>} A promise that resolves with the fetched consultation data
 */
export const getConsultationData = createAsyncThunk(
  ConsultationDataActions.getConsultationData,
  async (appointmentId: string, { rejectWithValue }) => {
    try {
      const { data } = await ConsultationDataAPI.getConsultationData(
        appointmentId,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Saves consultation data for a given appointment ID
 *
 * @param {object} params - An object containing the appointment ID and the data to be saved
 * @param {string} params.appointmentId - The ID of the appointment to save data for
 * @param {AppointmentDataCreationDto} params.data - The data to be saved
 * @returns {Promise<any>} A promise that resolves with the saved consultation data
 */
export const saveConsultationData = createAsyncThunk(
  ConsultationDataActions.saveConsultationData,
  async (
    params: { appointmentId: string; data: AppointmentDataCreationDto },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.saveConsultationData(
        params.appointmentId,
        params.data,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches consultation data history for a given appointment ID
 *
 * @param {string} appointmentId - The ID of the appointment to fetch history for
 * @returns {Promise<any>} A promise that resolves with the fetched consultation data history
 */
export const getConsultationDataHistory = createAsyncThunk(
  ConsultationDataActions.getConsultationDataHistory,
  async (appointmentId: string, { rejectWithValue }) => {
    try {
      const { data } = await ConsultationDataAPI.getConsultationDataHistory(
        appointmentId,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the medical background for a given patient ID
 *
 * @param {string} patientId - The ID of the patient to fetch the medical background for
 * @returns {Promise<any>} A promise that resolves with the fetched medical background data
 */
export const getPatientMedicalBackground = createAsyncThunk(
  ConsultationDataActions.getPatientMedicalBackground,
  async (patientId: string, { rejectWithValue }) => {
    try {
      const { data } = await ConsultationDataAPI.getPatientMedicalBackground(
        patientId,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches a list of allergies with pagination and search capabilities
 *
 * @param {PaginationPropsWithSearch} params - An object containing the pagination and search parameters
 * @returns {Promise<any>} A promise that resolves with the fetched allergy data
 */
export const getAllergies = createAsyncThunk(
  ConsultationDataActions.getAllergies,
  async (params: PaginationPropsWithSearch, { rejectWithValue }) => {
    try {
      const { data } = await ConsultationDataAPI.getAllergies(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches a list of diseases with pagination and search capabilities
 *
 * @param {PaginationPropsWithSearch} params - An object containing the pagination and search parameters
 * @returns {Promise<any>} A promise that resolves with the fetched disease data
 */
export const getDiseases = createAsyncThunk(
  ConsultationDataActions.getDiseases,
  async (params: PaginationPropsWithSearch, { rejectWithValue }) => {
    try {
      const { data } = await ConsultationDataAPI.getDiseases(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches a list of surgeries with pagination and search capabilities
 *
 * @param {PaginationPropsWithSearch} params - An object containing the pagination and search parameters
 * @returns {Promise<any>} A promise that resolves with the fetched surgery data
 */
export const getSurgeries = createAsyncThunk(
  ConsultationDataActions.getSurgeries,
  async (params: PaginationPropsWithSearch, { rejectWithValue }) => {
    try {
      const { data } = await ConsultationDataAPI.getSurgeries(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches a list of symptoms with pagination and search capabilities
 *
 * @param {PaginationPropsWithSearch} params - An object containing the pagination and search parameters
 * @returns {Promise<any>} A promise that resolves with the fetched symptom data
 */
export const getSymptoms = createAsyncThunk(
  ConsultationDataActions.getSymptoms,
  async (params: PaginationPropsWithSearch, { rejectWithValue }) => {
    try {
      const { data } = await ConsultationDataAPI.getSymptoms(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Assigns an allergy to a patient
 *
 * @param {object} params - An object containing the patient ID and the allergy ID
 * @param {string} params.patientId - The ID of the patient to assign the allergy to
 * @param {string} params.allergyId - The ID of the allergy to assign
 * @returns {Promise<any>} A promise that resolves with the assigned allergy data
 */
export const setPatientAllergy = createAsyncThunk(
  ConsultationDataActions.setPatientAllergy,
  async (
    params: { patientId: string; allergyId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.setPatientAllergy(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Removes an allergy from a patient
 *
 * @param {object} params - An object containing the patient ID and the allergy ID
 * @param {string} params.patientId - The ID of the patient to remove the allergy from
 * @param {string} params.allergyId - The ID of the allergy to remove
 * @returns {Promise<any>} A promise that resolves with the removed allergy data
 */
export const removePatientAllergy = createAsyncThunk(
  ConsultationDataActions.removePatientAllergy,
  async (
    params: { patientId: string; allergyId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.removePatientAllergy(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Assigns a disease to a patient
 *
 * @param {object} params - An object containing the patient ID and the disease ID
 * @param {string} params.patientId - The ID of the patient to assign the disease to
 * @param {string} params.diseaseId - The ID of the disease to assign
 * @returns {Promise<any>} A promise that resolves with the assigned disease data
 */
export const setPatientDisease = createAsyncThunk(
  ConsultationDataActions.setPatientDisease,
  async (
    params: { patientId: string; diseaseId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.setPatientDisease(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Removes a disease from a patient
 *
 * @param {object} params - An object containing the patient ID and the disease ID
 * @param {string} params.patientId - The ID of the patient to remove the disease from
 * @param {string} params.diseaseId - The ID of the disease to remove
 * @returns {Promise<any>} A promise that resolves with the removed disease data
 */
export const removePatientDisease = createAsyncThunk(
  ConsultationDataActions.removePatientDisease,
  async (
    params: { patientId: string; diseaseId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.removePatientDisease(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Assigns family historical data to a patient
 *
 * @param {object} params - An object containing the patient ID and the family history data
 * @param {string} params.patientId - The ID of the patient to assign the family history data to
 * @param {FamilyHistoryCreationDto} params.data - The family history data to assign
 * @returns {Promise<any>} A promise that resolves with the assigned family historical data
 */
export const setPatientFamilyHistoricData = createAsyncThunk(
  ConsultationDataActions.setPatientFamilyHistoricData,
  async (
    params: { patientId: string; data: FamilyHistoryCreationDto },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.setPatientFamilyHistoricData(
        params.patientId,
        params.data,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates family historical data for a patient
 *
 * @param {object} params - An object containing the patient ID, family history ID, and the updated family history data
 * @param {string} params.patientId - The ID of the patient to update the family history data for
 * @param {string} params.familyHistoryId - The ID of the family history data to update
 * @param {FamilyHistoryCreationDto} params.data - The updated family history data
 * @returns {Promise<any>} A promise that resolves with the updated family historical data
 */
export const updatePatientFamilyHistoricData = createAsyncThunk(
  ConsultationDataActions.updatePatientFamilyHistoricData,
  async (
    params: {
      patientId: string;
      familyHistoryId: string;
      data: FamilyHistoryCreationDto;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } =
        await ConsultationDataAPI.updatePatientFamilyHistoricData(
          params.patientId,
          params.familyHistoryId,
          params.data,
        );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Assigns surgery data to a patient
 *
 * @param {object} params - An object containing the patient ID and the surgery data
 * @param {string} params.patientId - The ID of the patient to assign the surgery data to
 * @param {SurgeryCreationDto} params.data - The surgery data to assign
 * @returns {Promise<any>} A promise that resolves with the assigned surgery data
 */
export const setPatientSurgeryData = createAsyncThunk(
  ConsultationDataActions.setPatientSurgeryData,
  async (
    params: { patientId: string; data: SurgeryCreationDto },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.setPatientSurgeryData(
        params.patientId,
        params.data,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates surgery data for a patient
 *
 * @param {object} params - An object containing the patient ID, surgery data ID, and the updated surgery data
 * @param {string} params.patientId - The ID of the patient to update the surgery data for
 * @param {string} params.surgeryDataId - The ID of the surgery data to update
 * @param {SurgeryCreationDto} params.data - The updated surgery data
 * @returns {Promise<any>} A promise that resolves with the updated surgery data
 */
export const updatePatientSurgeryData = createAsyncThunk(
  ConsultationDataActions.updatePatientSurgeryData,
  async (
    params: {
      patientId: string;
      surgeryDataId: string;
      data: SurgeryCreationDto;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.updatePatientSurgeryData(
        params.patientId,
        params.surgeryDataId,
        params.data,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes family historical data for a patient
 *
 * @param {object} params - An object containing the patient ID and the family history ID to delete
 * @param {string} params.patientId - The ID of the patient to delete the family history data for
 * @param {string} params.familyHistoryId - The ID of the family history data to delete
 * @returns {Promise<any>} A promise that resolves with the deleted family historical data
 */
export const deletePatientFamilyHistoricData = createAsyncThunk(
  ConsultationDataActions.deletePatientFamilyHistoricData,
  async (
    params: {
      patientId: string;
      familyHistoryId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } =
        await ConsultationDataAPI.deletePatientFamilyHistoricData(
          params.patientId,
          params.familyHistoryId,
        );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes surgery data for a patient
 *
 * @param {object} params - An object containing the patient ID and the surgery data ID to delete
 * @param {string} params.patientId - The ID of the patient to delete the surgery data for
 * @param {string} params.surgeryDataId - The ID of the surgery data to delete
 * @returns {Promise<any>} A promise that resolves with the deleted surgery data
 */
export const deleteSurgeryData = createAsyncThunk(
  ConsultationDataActions.deleteSurgeryData,
  async (
    params: {
      patientId: string;
      surgeryDataId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ConsultationDataAPI.deleteSurgeryData(
        params.patientId,
        params.surgeryDataId,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Retrieves health metrics for a specific specialization
 *
 * @param {string} specializationId - The ID of the specialization to retrieve health metrics for
 * @returns {Promise<any>} A promise that resolves with the retrieved health metrics
 */
export const getHealthMetricsBySpecialization = createAsyncThunk(
  ConsultationDataActions.getHealthMetricsBySpecialization,
  async (specializationId: string, { rejectWithValue }) => {
    try {
      const { data } =
        await ConsultationDataAPI.getHealthMetricsBySpecialization(
          specializationId,
        );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
